import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import OfferCard from "@/components/base/OfferCard";
import { useAdminProducts } from "@/hooks/useAdminStore";
import { useAdminMarketplaces } from "@/hooks/useAdminMarketplaces";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const marketplaceIcons: Record<string, { icon: string; color: string }> = {
  Amazon: { icon: "ri-amazon-line", color: "text-orange-500" },
  "Mercado Livre": { icon: "ri-shopping-bag-3-line", color: "text-yellow-500" },
  Shopee: { icon: "ri-shopping-cart-2-line", color: "text-orange-600" },
  "Magazine Luiza": { icon: "ri-store-line", color: "text-blue-500" },
  Americanas: { icon: "ri-gift-line", color: "text-red-500" },
};

const marketplaceBadgeStyle: Record<string, string> = {
  Amazon: "bg-orange-50 text-orange-600 border-orange-200",
  "Mercado Livre": "bg-yellow-50 text-yellow-700 border-yellow-200",
  Shopee: "bg-orange-50 text-orange-700 border-orange-200",
  "Magazine Luiza": "bg-blue-50 text-blue-600 border-blue-200",
  Americanas: "bg-red-50 text-red-600 border-red-200",
};

export default function FeaturedProducts() {
  const { products } = useAdminProducts();
  const { activeMarketplaceOptions } = useAdminMarketplaces();
  const { settings } = useSiteSettings();
  const [activeTab, setActiveTab] = useState("Todos");
  const [activeMarketplace, setActiveMarketplace] = useState("Todos");

  const highlightedProducts = useMemo(
    () => products.filter((product) => product.showInFeatured),
    [products]
  );

  const tabs = useMemo(
    () => ["Todos", ...Array.from(new Set(highlightedProducts.map((product) => product.category).filter(Boolean)))],
    [highlightedProducts]
  );

  const marketplaceNames = useMemo(
    () => ["Todos", ...Array.from(new Set([...activeMarketplaceOptions, ...highlightedProducts.map((product) => product.marketplace)].filter(Boolean)))],
    [activeMarketplaceOptions, highlightedProducts]
  );

  const marketplaces = useMemo(
    () => marketplaceNames.map((name) => ({
      name,
      icon: marketplaceIcons[name]?.icon ?? "ri-store-2-line",
      color: marketplaceIcons[name]?.color ?? "text-gray-600",
    })),
    [marketplaceNames]
  );

  const filtered = highlightedProducts.filter((product) => {
    const matchCategory = activeTab === "Todos" || product.category === activeTab;
    const matchMarketplace = activeMarketplace === "Todos" || product.marketplace === activeMarketplace;
    return matchCategory && matchMarketplace;
  });

  const getMarketplaceCount = (name: string) => {
    if (name === "Todos") return highlightedProducts.length;
    return highlightedProducts.filter((product) => product.marketplace === name).length;
  };

  return (
    <section className="py-14">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900">Produtos em Destaque</h2>
            {settings.homeFeaturedDescription.trim() && (
              <p className="text-gray-500 text-sm mt-1">{settings.homeFeaturedDescription}</p>
            )}
          </div>

          <Link
            to="/categorias"
            className="hidden md:flex items-center gap-2 text-orange-500 hover:text-orange-600 font-semibold text-sm transition-colors"
          >
            Ver todos os produtos
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                activeTab === tab
                  ? "bg-orange-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap mr-1">
            Marketplace:
          </span>
          {marketplaces.map((marketplace) => {
            const count = getMarketplaceCount(marketplace.name);
            const isActive = activeMarketplace === marketplace.name;
            return (
              <button
                key={marketplace.name}
                onClick={() => setActiveMarketplace(marketplace.name)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer border ${
                  isActive
                    ? marketplace.name === "Todos"
                      ? "bg-gray-800 text-white border-gray-800"
                      : `${marketplaceBadgeStyle[marketplace.name] ?? "bg-gray-100 text-gray-700 border-gray-200"} border-2`
                    : "bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                <span className={`w-4 h-4 flex items-center justify-center ${isActive && marketplace.name !== "Todos" ? marketplace.color : ""}`}>
                  <i className={`${marketplace.icon} text-xs`}></i>
                </span>
                {marketplace.name}
                <span
                  className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    isActive
                      ? marketplace.name === "Todos"
                        ? "bg-white/20 text-white"
                        : "bg-gray-900/10 text-gray-700"
                      : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-200 bg-gray-50 py-16 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white mb-4 border border-gray-200">
              <i className="ri-star-line text-2xl text-gray-400"></i>
            </div>
            <p className="text-gray-500 font-semibold">
              {highlightedProducts.length === 0 ? "Nenhum destaque habilitado ainda" : "Nenhum produto encontrado"}
            </p>
            <p className="text-gray-400 text-sm mt-1">
              {highlightedProducts.length === 0
                ? "Habilite produtos no cadastro ou no painel /admin/destaques para preencher esta seção automaticamente."
                : "Tente combinar outros filtros de categoria e marketplace."}
            </p>
            {highlightedProducts.length > 0 && (
              <button
                onClick={() => {
                  setActiveTab("Todos");
                  setActiveMarketplace("Todos");
                }}
                className="mt-4 px-4 py-2 rounded-full bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Limpar filtros
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 items-start">
            {filtered.map((product) => (
              <OfferCard
                key={product.id}
                item={{
                  slug: product.slug,
                  image: product.image,
                  name: product.name,
                  price: product.price,
                  originalPrice: product.originalPrice,
                  discount: product.discount,
                  marketplace: product.marketplace,
                  freeShipping: product.freeShipping,
                  fulfillment: product.fulfillment,
                  officialStoreName: product.officialStoreName || (product.officialStore ? "Loja Oficial" : ""),
                  affiliateLink: product.affiliateLink,
                  isFlash: product.badge === "Oferta Relâmpago",
                  category: product.category,
                  categorySlug: product.categorySlug,
                  subcategory: product.subcategory,
                  subcategorySlug: product.subcategorySlug,
                }}
                className="w-full"
              />
            ))}
          </div>
        )}

        <div className="mt-10 rounded-2xl bg-gradient-to-r from-stone-800 to-stone-900 p-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-orange-500/20">
              <i className="ri-advertisement-line text-orange-400 text-xl"></i>
            </div>
            <div>
              <p className="text-white font-bold">Espaço Publicitário</p>
              <p className="text-stone-400 text-sm">Banner 728x90 — Google AdSense</p>
            </div>
          </div>
          <div className="w-full md:w-auto h-16 md:w-96 bg-stone-700 rounded-xl flex items-center justify-center border border-dashed border-stone-600">
            <span className="text-stone-500 text-xs">ADS 728x90</span>
          </div>
        </div>
      </div>
    </section>
  );
}